/**
 * Module 2 — Quality of Earnings panel. Shows the three-EBITDA bridge (Reported → Adjusted →
 * Defensible) per actual fiscal year, revenue quality, working capital, margins, and the flag
 * register. Recomputes from classified_lines on open (edit-cascade from Module 1 is automatic).
 */

import React, { useEffect, useState } from 'react';
import { qofeApi } from '../services/api';
import { Button } from './ui';
import { getAttestations, getAttestation } from '../services/attestationApi';
import RequestAttestationModal from './attestation/RequestAttestationModal';
import AttestationStatusCard from './attestation/AttestationStatusCard';
import AttestationDetailModal from './attestation/AttestationDetailModal';

const ACCENT = '#161f4a';
const ANCHOR_BG = '#eef2ff';
const money = (n) => (n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));
const pct = (n) => (n == null ? '—' : `${(n * 100).toFixed(1)}%`);

// Rows of the EBITDA bridge, rendered as one column per fiscal year.
// `sellerFmt` (when present) is the seller-claimed value for the side-by-side compare.
const METRIC_ROWS = [
  { label: 'Reported EBITDA', fmt: (pb) => money(pb.reported_ebitda), sellerFmt: (pb) => money(pb.seller && pb.seller.unadjusted_ebitda) },
  { label: '+ Addbacks', fmt: (pb) => money(pb.total_addbacks), green: true, sellerFmt: (pb) => money(pb.seller && pb.seller.total_addbacks) },
  { label: 'Adjusted EBITDA', fmt: (pb) => money(pb.adjusted_ebitda), strong: true, sellerFmt: (pb) => money(pb.seller && pb.seller.adjusted_ebitda) },
  { label: 'Defensible EBITDA', fmt: (pb) => money(pb.defensible_ebitda), strong: true },
  { label: 'EBITDA margin', fmt: (pb) => pct(pb.ebitda_margin), muted: true },
  { label: 'Revenue', fmt: (pb) => money(pb.total_net_revenue), muted: true, sellerFmt: (pb) => money(pb.seller && pb.seller.total_revenue) },
];

const SELLER_GREY = '#6b7280';

export default function QofEPanel({ dealId }) {
  const [state, setState] = useState({ loading: true });
  const [attestation, setAttestation] = useState({ requests: [], dealnexaCpa: { available: false, firmName: null } });
  const [showModal, setShowModal] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [attestedDetail, setAttestedDetail] = useState(null);
  const [attestedDetailFetched, setAttestedDetailFetched] = useState(null); // tracks the id we fetched for
  const [addbacksOpen, setAddbacksOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await qofeApi.get(dealId);
        if (alive) setState({ loading: false, data: res.data });
      } catch (e) {
        const msg = e?.response?.data?.message || 'Failed to compute QofE.';
        if (alive) setState({ loading: false, error: msg });
      }
    })();
    return () => { alive = false; };
  }, [dealId]);

  // Load attestation state — supplementary; never crashes QofE panel on failure.
  const loadAttestations = () => {
    getAttestations(dealId)
      .then((data) => setAttestation(data))
      .catch(() => {}); // attestation is optional; ignore errors
  };

  useEffect(() => {
    loadAttestations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // Fetch attested detail (snapshot) only once per attested request id — never refetches in a loop.
  useEffect(() => {
    const latest = attestation.requests[0];
    if (!latest || latest.status !== 'attested') return;
    if (attestedDetailFetched === latest.id) return; // already fetched
    setAttestedDetailFetched(latest.id);
    getAttestation(dealId, latest.id)
      .then((detail) => setAttestedDetail(detail))
      .catch(() => {}); // divergence note is non-blocking
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, attestation.requests]);

  if (state.loading) return <div style={{ padding: 20, color: '#64748b' }}>Computing Quality of Earnings…</div>;
  if (state.error) return <div style={{ padding: 20, color: '#b91c1c' }}>{state.error}</div>;
  if (!state.data || state.data.status === 'no_data')
    return <div style={{ padding: 20, color: '#64748b' }}>No classified financials yet — run “Classify Financials” first.</div>;

  const q = state.data.qofe;

  // Backend may halt (e.g. no actual P&L / proforma-only) and return flags but no bridge.
  if (q.halted) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ padding: '10px 12px', borderRadius: 8, background: '#fef2f2', border: '1px solid #b91c1c', color: '#b91c1c', fontSize: 13, fontWeight: 500 }}>
          Quality of Earnings could not be computed — {q.flags?.[0]?.detail || q.halt_reason}
        </div>
        <FlagList flags={q.flags} />
      </div>
    );
  }

  const b = q.bridge;
  const periods = (q.period_bridges && q.period_bridges.length) ? q.period_bridges : [b];

  // Per-year addback matrix: one row per distinct GL label, amount + support per period.
  // Each year's cells sum to that period's total_addbacks by construction (same array).
  const hasAddbackDetail = periods.some((pb) => (pb.addbacks || []).length > 0);
  const addbackRows = (() => {
    const order = [];
    const byLabel = new Map();
    periods.forEach((pb) => {
      (pb.addbacks || []).forEach((a) => {
        if (!byLabel.has(a.label)) { byLabel.set(a.label, {}); order.push(a.label); }
        const cells = byLabel.get(a.label);
        const prev = cells[pb.period] || { amount: 0, allDocumented: true };
        cells[pb.period] = {
          amount: prev.amount + (Number(a.amount) || 0),
          allDocumented: prev.allDocumented && a.support_level === 'documented',
        };
      });
    });
    return order.map((label) => ({ label, cells: byLabel.get(label) }));
  })();

  // Seller's claimed bridge (extracted from source docs) for the side-by-side compare.
  const hasSeller = periods.some((pb) => pb && pb.seller);
  const hasSellerAddbackDetail = periods.some((pb) => pb && pb.seller && (pb.seller.addbacks || []).length > 0);
  const sellerAddbackRows = (() => {
    if (!hasSellerAddbackDetail) return [];
    const order = [];
    const byLabel = new Map();
    periods.forEach((pb) => {
      ((pb.seller && pb.seller.addbacks) || []).forEach((a) => {
        if (!byLabel.has(a.label)) { byLabel.set(a.label, {}); order.push(a.label); }
        const cells = byLabel.get(a.label);
        cells[pb.period] = { amount: (cells[pb.period]?.amount || 0) + (Number(a.amount) || 0) };
      });
    });
    return order.map((label) => ({ label, cells: byLabel.get(label) }));
  })();

  // Derive supporting docs from citations in the QofE result if available.
  const supportingDocs = (() => {
    try {
      const citations = state.data?.qofe?.citations || [];
      const seen = new Set();
      return citations
        .filter((c) => c && c.document_id && c.document_name)
        .filter((c) => { if (seen.has(c.document_id)) return false; seen.add(c.document_id); return true; })
        .map((c) => ({ id: c.document_id, filename: c.document_name }));
    } catch (_) {
      return [];
    }
  })();

  const activeRequest = attestation.requests[0] || null;

  // Compute divergence between attested snapshot and live Defensible EBITDA.
  // Use the same accessor as the EBITDA bridge: period_bridges (or bridge fallback), anchor = q.period.
  const divergenceNote = (() => {
    if (!activeRequest || activeRequest.status !== 'attested' || !attestedDetail) return null;
    try {
      const snapQ = attestedDetail?.qofe_snapshot?.qofe;
      if (!snapQ) return null;
      const snapPeriods = (snapQ.period_bridges && snapQ.period_bridges.length) ? snapQ.period_bridges : [snapQ.bridge];
      const snapAnchor = snapPeriods.find((pb) => pb?.period === snapQ.period) || snapPeriods[0];
      const snapDefensible = snapAnchor?.defensible_ebitda;

      const livePeriods = periods; // already computed above (period_bridges or [b])
      const liveAnchor = livePeriods.find((pb) => pb?.period === q.period) || livePeriods[0];
      const liveDefensible = liveAnchor?.defensible_ebitda;

      if (snapDefensible == null || liveDefensible == null) return null;
      if (Math.abs(liveDefensible - snapDefensible) <= 1) return null;

      const attestedAt = attestedDetail.attested_at || activeRequest.attested_at;
      const dateStr = attestedAt
        ? new Date(attestedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : 'attested version';
      return `Live numbers differ from the attested version (${dateStr}).`;
    } catch (_) {
      return null;
    }
  })();

  return (
    <div style={{ padding: 16 }}>
      {/* Attestation entry point */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: divergenceNote ? 8 : 16 }}>
        {activeRequest ? (
          <AttestationStatusCard
            request={activeRequest}
            onView={() => setDetailOpen(true)}
          />
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowModal(true)}
          >
            Request CPA Attestation
          </Button>
        )}
      </div>
      {divergenceNote && (
        <div style={{
          marginBottom: 16, padding: '6px 10px', borderRadius: 6,
          background: '#FFF3CD', border: '1px solid #FFC107',
          color: '#7a5b00', fontSize: 12,
        }}>
          {divergenceNote}
        </div>
      )}

      {/* Create fork — only when no request exists */}
      {showModal && !activeRequest && (
        <RequestAttestationModal
          dealId={dealId}
          dealnexaCpa={attestation.dealnexaCpa}
          supportingDocs={supportingDocs}
          onClose={() => setShowModal(false)}
          onCreated={() => {
            setShowModal(false);
            loadAttestations();
          }}
        />
      )}

      {/* Detail modal — only when a request exists */}
      {detailOpen && activeRequest && (
        <AttestationDetailModal
          dealId={dealId}
          attestationId={activeRequest.id}
          onClose={() => setDetailOpen(false)}
        />
      )}

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <section style={{ minWidth: 280, overflowX: 'auto' }}>
          <h4 style={{ margin: '0 0 8px' }}>
            EBITDA Bridge{' '}
            <span style={{ color: '#94a3b8', fontWeight: 400 }}>
              {periods.length > 1 ? `(${periods.length} actual fiscal years)` : `(${q.period})`}
            </span>
          </h4>
          {hasSeller && (
            <div style={{ margin: '0 0 8px', fontSize: 11, color: '#94a3b8' }}>
              Each cell: <span style={{ color: ACCENT, fontWeight: 600 }}>DealNexa</span> (top) ·{' '}
              <span style={{ color: SELLER_GREY }}>Seller (CIM)</span> (bottom)
            </div>
          )}
          <table style={{ borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr>
                <th aria-label="metric" style={{ padding: '6px 8px' }} />
                {periods.map((pb) => {
                  const isAnchor = pb.period === q.period;
                  return (
                    <th key={pb.period} style={{
                      padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap',
                      color: isAnchor ? ACCENT : '#64748b', fontWeight: isAnchor ? 700 : 600,
                      background: isAnchor ? ANCHOR_BG : 'transparent',
                    }}>{pb.period}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {METRIC_ROWS.map((row) => {
                const isAddbackRow = row.label === '+ Addbacks';
                const expandable = isAddbackRow && (hasAddbackDetail || hasSellerAddbackDetail);
                return (
                  <React.Fragment key={row.label}>
                    <tr style={{ borderBottom: '1px solid #f1f5f9', fontWeight: row.strong ? 600 : 400 }}>
                      <td style={{ padding: '6px 8px', color: row.muted ? '#64748b' : '#334155', whiteSpace: 'nowrap' }}>
                        {expandable ? (
                          <button
                            type="button"
                            onClick={() => setAddbacksOpen((o) => !o)}
                            aria-expanded={addbacksOpen}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: ACCENT, display: 'inline-flex', alignItems: 'center', gap: 5 }}
                          >
                            <span style={{ fontSize: 9, display: 'inline-block', transform: addbacksOpen ? 'rotate(90deg)' : 'none', transition: 'transform 120ms' }}>▶</span>
                            {row.label}
                          </button>
                        ) : row.label}
                      </td>
                      {periods.map((pb) => {
                        const isAnchor = pb.period === q.period;
                        const showSeller = hasSeller && row.sellerFmt;
                        return (
                          <td key={pb.period} style={{
                            padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap',
                            background: isAnchor ? ANCHOR_BG : 'transparent',
                            color: row.green ? '#15803d' : row.muted ? '#64748b' : ACCENT,
                          }}>
                            <div>{row.fmt(pb)}</div>
                            {showSeller && (
                              <div style={{ color: SELLER_GREY, fontWeight: 400, fontSize: 12 }}>{row.sellerFmt(pb)}</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>

                    {row.label === 'Adjusted EBITDA' && hasSeller && (
                      <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '4px 8px', color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>Δ vs seller</td>
                        {periods.map((pb) => {
                          const isAnchor = pb.period === q.period;
                          const ind = pb.adjusted_ebitda;
                          const sel = pb.seller && pb.seller.adjusted_ebitda;
                          const delta = (ind != null && sel != null) ? ind - sel : null;
                          const material = delta != null && Math.abs(delta) / Math.max(Math.abs(ind), Math.abs(sel), 1) > 0.05;
                          return (
                            <td key={pb.period} style={{
                              padding: '4px 10px', textAlign: 'right', whiteSpace: 'nowrap', fontSize: 12,
                              background: isAnchor ? ANCHOR_BG : 'transparent',
                              color: material ? '#b45309' : '#94a3b8',
                            }}>{delta == null ? '—' : money(delta)}</td>
                          );
                        })}
                      </tr>
                    )}

                    {expandable && addbacksOpen && addbackRows.map((ar) => (
                      <tr key={`ab-${ar.label}`} style={{ background: '#fafbff', fontSize: 12 }}>
                        <td style={{ padding: '4px 8px 4px 24px', color: '#475569', whiteSpace: 'nowrap' }}>{ar.label}</td>
                        {periods.map((pb) => {
                          const isAnchor = pb.period === q.period;
                          const cell = ar.cells[pb.period];
                          return (
                            <td key={pb.period} style={{
                              padding: '4px 10px', textAlign: 'right', whiteSpace: 'nowrap',
                              background: isAnchor ? ANCHOR_BG : 'transparent',
                            }}>
                              {cell ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end', color: '#15803d' }}>
                                  {money(cell.amount)}
                                  <span title={cell.allDocumented ? 'Documented' : 'Asserted'} aria-label={cell.allDocumented ? 'Documented' : 'Asserted'} style={{ fontSize: 9, color: cell.allDocumented ? '#15803d' : '#cbd5e1' }}>
                                    {cell.allDocumented ? '●' : '○'}
                                  </span>
                                </span>
                              ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    {expandable && addbacksOpen && hasAddbackDetail && (
                      <tr style={{ background: '#fafbff' }}>
                        <td colSpan={periods.length + 1} style={{ padding: '2px 8px 8px 24px', fontSize: 11, color: '#94a3b8' }}>
                          <span style={{ color: '#15803d' }}>●</span> documented (drives Defensible EBITDA)&nbsp; · &nbsp;<span style={{ color: '#cbd5e1' }}>○</span> asserted
                        </td>
                      </tr>
                    )}

                    {expandable && addbacksOpen && hasSellerAddbackDetail && (
                      <tr style={{ background: '#fafbff' }}>
                        <td colSpan={periods.length + 1} style={{ padding: '8px 8px 2px 24px', fontSize: 11, fontWeight: 600, color: SELLER_GREY, borderTop: '1px solid #eef2f7' }}>
                          Seller (CIM) — claimed addbacks <span style={{ fontWeight: 400, color: '#cbd5e1' }}>(asserted)</span>
                        </td>
                      </tr>
                    )}
                    {expandable && addbacksOpen && sellerAddbackRows.map((ar) => (
                      <tr key={`sab-${ar.label}`} style={{ background: '#fafbff', fontSize: 12 }}>
                        <td style={{ padding: '4px 8px 4px 24px', color: SELLER_GREY, whiteSpace: 'nowrap' }}>{ar.label}</td>
                        {periods.map((pb) => {
                          const isAnchor = pb.period === q.period;
                          const cell = ar.cells[pb.period];
                          return (
                            <td key={pb.period} style={{
                              padding: '4px 10px', textAlign: 'right', whiteSpace: 'nowrap',
                              background: isAnchor ? ANCHOR_BG : 'transparent',
                            }}>
                              {cell ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end', color: SELLER_GREY }}>
                                  {money(cell.amount)}
                                  <span title="Asserted" aria-label="Asserted" style={{ fontSize: 9, color: '#cbd5e1' }}>○</span>
                                </span>
                              ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    {expandable && addbacksOpen && hasSellerAddbackDetail && (() => {
                      const nonFooting = periods
                        .filter((pb) => pb.seller && pb.seller.itemized && pb.seller.addbacks_reconciles === false)
                        .map((pb) => pb.period);
                      if (!nonFooting.length) return null;
                      return (
                        <tr style={{ background: '#fafbff' }}>
                          <td colSpan={periods.length + 1} style={{ padding: '2px 8px 8px 24px', fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                            Seller itemized detail does not foot to the stated adjustments for {nonFooting.join(', ')} — the seller's headline “Adjustments to EBITDA” is a curated non-recurring / discretionary subset.
                          </td>
                        </tr>
                      );
                    })()}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </section>

        <section style={{ minWidth: 220 }}>
          <h4 style={{ margin: '0 0 8px' }}>Quality metrics <span style={{ color: '#94a3b8', fontWeight: 400 }}>({q.period})</span></h4>
          <Metric label="Recurring revenue %" value={pct(q.revenue_quality.recurring_pct)} />
          <Metric label="Gross margin" value={pct(q.margin_cost.gross_margin)} />
          <Metric label="Personnel % rev" value={pct(q.margin_cost.personnel_pct_revenue)} />
          <Metric label="Occupancy % rev" value={pct(q.margin_cost.occupancy_pct_revenue)} />
          <Metric label="Net working capital" value={money(q.working_capital.net_working_capital)} />
          <Metric label="D&A % rev" value={pct(q.working_capital.da_pct_of_revenue)} />
        </section>
      </div>

      <FlagList flags={q.flags} />
    </div>
  );
}

function FlagList({ flags = [] }) {
  return (
    <>
      <h4 style={{ margin: '18px 0 6px' }}>Earnings-quality flags ({flags.length})</h4>
      {flags.length === 0 ? (
        <div style={{ color: '#15803d', fontSize: 13 }}>No flags raised.</div>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
          {flags.map((f, i) => (
            <li key={i} style={{ marginBottom: 6 }}>
              <strong style={{ color: (f.severity === 'CRITICAL' || f.severity === 'HIGH') ? '#b91c1c' : '#b45309' }}>{f.title}</strong>{' — '}
              {f.detail} {f.adjustment ? <em>({money(f.adjustment)})</em> : null}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function Metric({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', borderBottom: '1px solid #f8fafc' }}>
      <span style={{ color: '#64748b' }}>{label}</span><span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}
